import classNames from 'classnames';
import { Modal, PopupView } from 'lego-on-react';
import * as React from 'react';
import { CSSProperties } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { PopupScopeContext } from '../../service/Dom/Dom';
import { LazyVisible } from '../LazyVisible/LazyVisible';
import { Loader } from '../Loader/Loader';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './ModalDecorator.css';

interface ModalDecoratorBaseProps {
    hasClose?: boolean;
    progress?: boolean;
    onClose?: () => void;
    cls?: string;
    modalCls?: string;
    bodyCls?: string;
    headerHint?: React.ReactNode;
    visible: boolean;
    footer?: React.ReactNode;
    header?: React.ReactNode;
    autoclosable?: boolean;
    onOutsideClick?: () => void;
    theme?:
        | 'normal'
        | 'clear'
        | 'default'
        | 'full-width-body'
        | 'full-width-body-with-borders'
        | 'dark-paranja';
    view?: PopupView;
    zIndexGroupLevel?: number;
}

interface ModalDecoratorScrollableProps extends ModalDecoratorBaseProps {
    scrollable: true;
    fixedWidth: number;
}

interface ModalDecoratorNonScrollableProps extends ModalDecoratorBaseProps {
    scrollable?: false;
    fixedWidth?: never;
}

export type ModalDecoratorProps =
    | ModalDecoratorScrollableProps
    | ModalDecoratorNonScrollableProps;

export interface ModalDecoratorState {
    inited: boolean;
}

const BLOCK = 'modal-decorator';

export class ModalDecorator extends React.Component<
    ModalDecoratorProps,
    ModalDecoratorState
> {
    static getDerivedStateFromProps(
        nextProps: ModalDecoratorProps,
        prevState: ModalDecoratorState
    ) {
        if (nextProps.visible) {
            return {
                ...prevState,
                inited: true
            };
        }

        return null;
    }

    constructor(props: ModalDecoratorProps) {
        super(props);

        this.state = { inited: false };
    }

    render() {
        const {
            cls,
            modalCls,
            bodyCls,
            hasClose,
            visible,
            autoclosable,
            onOutsideClick,
            theme = 'default',
            view
        } = this.props;

        const contentStyle: CSSProperties = {};
        let scrollableMod = false;

        // для модалок с прокруткой
        if (this.props.scrollable) {
            const { fixedWidth } = this.props;
            // Чтобы скроллбар располагался внутри ширины модального окна
            // нужно зафиксировать ширину
            contentStyle.width = fixedWidth;
            // Остальные правила в css
            scrollableMod = true;
            // contentStyle.paddingRight = 10;
        }

        return (
            <LazyVisible>
                <Modal
                    cls={classNames(
                        Bem.cls({
                            block: BLOCK,
                            mods: { theme }
                        }),
                        modalCls
                    )}
                    visible={visible}
                    autoclosable={autoclosable}
                    onOutsideClick={onOutsideClick}
                    theme={'normal'}
                    view={view}
                >
                    <div
                        className={classNames(
                            Bem.cls(BLOCK, 'content', {
                                scrollable: scrollableMod
                            }),
                            cls
                        )}
                        style={contentStyle}
                    >
                        {this.getHeader()}

                        <div
                            className={classNames(
                                Bem.cls(BLOCK, 'body'),
                                bodyCls
                            )}
                        >
                            <PopupScopeContext.Provider
                                value={() => document.body}
                            >
                                {this.props.children}
                            </PopupScopeContext.Provider>
                        </div>

                        {this.getFooter()}

                        {hasClose ? (
                            <span
                                className={Bem.cls(BLOCK, 'close')}
                                onClick={this.onClose}
                            >
                                <Shape2
                                    width={16}
                                    height={16}
                                    icon={Shape2Icon.close}
                                    theme={Shape2Theme.light}
                                />
                            </span>
                        ) : null}
                    </div>
                    {this.getSpinner()}
                </Modal>
            </LazyVisible>
        );
    }

    private getSpinner() {
        const { progress } = this.props;

        if (!progress) {
            return null;
        }

        return <Loader cls={Bem.cls(BLOCK, 'loader')} size={'l'} />;
    }

    private getFooter() {
        const { footer } = this.props;

        if (!footer) {
            return null;
        }

        return <div className={Bem.cls(BLOCK, 'footer')}>{footer}</div>;
    }

    private getHeader() {
        const { header } = this.props;

        if (!header) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'header')}>
                <div className={Bem.cls(BLOCK, 'title')}>{header}</div>

                {this.getModalHint()}
            </div>
        );
    }

    private getModalHint() {
        const { headerHint } = this.props;

        if (!headerHint) {
            return null;
        }

        return <p className={Bem.cls(BLOCK, 'header-hint')}>{headerHint}</p>;
    }

    private onClose = () => {
        const { onClose } = this.props;

        if (onClose) {
            onClose();
        }
    };
}
